import type { CurrencyConfig, StockStatus } from '@/types'

import { CURRENCIES, DEFAULT_CURRENCY } from '@/constants/inventory'

const NBSP = ' '
const MINUS = '−'

export const getCurrency = (code: string | null | undefined): CurrencyConfig => {
    return CURRENCIES.find(currency => currency.code === code)
        ?? CURRENCIES.find(currency => currency.code === DEFAULT_CURRENCY)
        ?? CURRENCIES[0]
}

const groupDigits = (digits: string, separator: string): string => {
    let result = ''

    for (let index = 0; index < digits.length; index++) {
        const remaining = digits.length - index

        result += digits[index]

        if (remaining > 1 && (remaining - 1) % 3 === 0) result += separator
    }

    return result
}

const toFinite = (value: number): number => Number.isFinite(value) ? value : 0

export const formatNumber = (value: number, currency: CurrencyConfig, decimals = 0): string => {
    const safe = toFinite(value)
    const fixed = Math.abs(safe).toFixed(decimals)
    const [integer, fraction] = fixed.split('.')
    const isNegative = safe < 0 && Number(fixed) !== 0
    const grouped = groupDigits(integer, currency.groupSeparator)
    const body = fraction ? `${grouped}${currency.decimalSeparator}${fraction}` : grouped

    return isNegative ? `${MINUS}${body}` : body
}

export const formatMoney = (value: number, currency: CurrencyConfig): string => {
    const safe = toFinite(value)
    const body = formatNumber(Math.abs(safe), currency, currency.decimals)
    const isNegative = safe < 0 && Number(Math.abs(safe).toFixed(currency.decimals)) !== 0
    const sign = isNegative ? MINUS : ''

    if (currency.symbolPosition === 'after') return `${sign}${body}${NBSP}${currency.symbol}`

    return `${sign}${currency.symbol}${currency.spaced ? NBSP : ''}${body}`
}

export const formatSigned = (value: number, currency: CurrencyConfig): string => {
    if (value > 0) return `+${formatNumber(value, currency)}`
    if (value < 0) return `${MINUS}${formatNumber(Math.abs(value), currency)}`

    return '0'
}

export const formatPercent = (value: number, currency: CurrencyConfig): string => {
    const decimals = Math.abs(value) >= 10 || Number.isInteger(value) ? 0 : 1

    return `${formatNumber(value, currency, decimals)}${NBSP}%`
}

export const getStockStatus = (quantity: number, minStock: number): StockStatus => {
    if (quantity <= 0) return 'out'
    if (quantity <= minStock) return 'low'

    return 'ok'
}

export const normalizeText = (value: string | null | undefined): string => {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
}

export const pluralize = (count: number, singular: string, plural: string): string => {
    return count === 1 ? singular : plural
}
